/**
 * Normalize brief from array format to object format.
 *
 * Accepts either:
 *   - Object `{q1: "Yes", q2: "Bluehost"}` → returned as-is
 *   - Array  `[{question_id: "q1", answer: "Yes"}, ...]` → converted to object
 *
 * Returns `null` for invalid formats (not object, not valid array).
 */
export function normalizeBrief(brief) {
    if (brief === null || brief === undefined || typeof brief !== 'object') {
        return null;
    }
    // Already an object (non-array) — pass through
    if (!Array.isArray(brief)) {
        return brief;
    }
    // Array format — convert each item
    const result = {};
    for (const item of brief) {
        if (item === null ||
            typeof item !== 'object' ||
            Array.isArray(item) ||
            typeof item.question_id !== 'string' ||
            !item.question_id) {
            return null;
        }
        const typed = item;
        // For multi_select: prefer choices array over comma-joined answer string
        const value = Array.isArray(typed.choices) && typed.choices.length > 0
            ? typed.choices
            : typed.answer;
        result[typed.question_id] = value;
    }
    return result;
}
/**
 * Evaluate whether a question's showWhen condition is met (i.e., the field is visible).
 * If there's no showWhen, the field is always visible.
 */
export function isFieldVisible(showWhen, answers) {
    if (!showWhen)
        return true;
    const depValue = answers[showWhen.questionId];
    if (showWhen.isNotEmpty) {
        if (depValue === undefined || depValue === null || depValue === '')
            return false;
        if (Array.isArray(depValue) && depValue.length === 0)
            return false;
        return true;
    }
    if (showWhen.equals !== undefined) {
        const targets = Array.isArray(showWhen.equals) ? showWhen.equals : [showWhen.equals];
        if (Array.isArray(depValue)) {
            return targets.some((t) => depValue.includes(t));
        }
        return targets.includes(depValue);
    }
    return true;
}
/**
 * Validate brief answers against a playbook schema.
 *
 * Pure function — no DB dependency. Returns all issues at once so callers
 * (like Coach Grace) can re-ask only what's needed.
 */
export function validateBriefAnswers(schema, brief) {
    const missing_fields = [];
    const invalid_fields = [];
    // brief must be a plain object
    if (brief === null ||
        brief === undefined ||
        typeof brief !== 'object' ||
        Array.isArray(brief)) {
        invalid_fields.push({ id: '_root', message: 'Brief must be a plain object' });
        return { valid: false, missing_fields, invalid_fields };
    }
    const answers = brief;
    // Flatten sections → questions
    const questions = schema.flatMap((s) => s.questions);
    const questionIds = new Set(questions.map((q) => q.id));
    // Check for unknown keys
    for (const key of Object.keys(answers)) {
        if (!questionIds.has(key)) {
            invalid_fields.push({ id: key, message: 'Unknown field' });
        }
    }
    // Validate each question
    for (const q of questions) {
        const value = answers[q.id];
        const visible = isFieldVisible(q.showWhen, answers);
        // Type enforcement
        if (value !== undefined && value !== null && value !== '') {
            if (q.fieldType === 'multi_select') {
                if (!Array.isArray(value)) {
                    invalid_fields.push({
                        id: q.id,
                        message: 'Must be an array for multi-select field',
                    });
                    continue; // skip further checks for this field
                }
                // Each element must be a string
                if (value.some((v) => typeof v !== 'string')) {
                    invalid_fields.push({
                        id: q.id,
                        message: 'All values must be strings',
                    });
                    continue;
                }
                // Option validation
                if (q.options && q.options.length > 0) {
                    const invalidValues = value.filter((v) => !q.options.includes(v));
                    if (invalidValues.length > 0) {
                        invalid_fields.push({
                            id: q.id,
                            message: `Invalid option(s): ${invalidValues.join(', ')}. Must select from available options`,
                        });
                        continue;
                    }
                }
            }
            else {
                // short_text, long_text, single_select → must be string
                if (typeof value !== 'string') {
                    invalid_fields.push({
                        id: q.id,
                        message: 'Must be a string',
                    });
                    continue;
                }
                // Option validation for single_select
                if (q.fieldType === 'single_select' && q.options && q.options.length > 0) {
                    if (!q.options.includes(value)) {
                        invalid_fields.push({
                            id: q.id,
                            message: `Invalid option: ${value}. Must select from available options`,
                        });
                        continue;
                    }
                }
            }
        }
        // Required check (default true) — only for visible fields
        const isRequired = q.required !== false;
        if (isRequired && visible) {
            const isEmpty = value === undefined ||
                value === null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0);
            if (isEmpty) {
                missing_fields.push({
                    id: q.id,
                    question: q.question,
                    fieldType: q.fieldType,
                });
            }
        }
    }
    return {
        valid: missing_fields.length === 0 && invalid_fields.length === 0,
        missing_fields,
        invalid_fields,
    };
}
//# sourceMappingURL=brief.js.map